import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { toClientIdentifiedReviewEvent } from "components/manualReview/utils/reviewSerialization";
import { expectedSlotRevisions } from "components/manualReview/utils/reviewRevisions";
import { ReviewRefreshRequiredError } from "../utils/reviewErrors";
import {
  canonicalInstant,
  dedupeClientIdentifiedEvents,
} from "components/manualReview/utils/eventUtils";
import { dedupeById } from "components/manualReview/utils/editScope";
import { useCallback, useRef, useState } from "react";
import { EpochMs } from "hooks/useActivityTimer";
import {
  ReviewPeriodData,
  ReviewSlotRevision,
  useUploadCorrectedEventsAndVerifyMutation,
} from "src/store/api/generatedApi";

export const useVerifyPeriods = ({
  tenantName,
  location,
  getElapsedSeconds,
  resetTimer,
  sessionStartedAt,
  reviewSlotRevisions,
}: {
  tenantName: string;
  location: string;
  getElapsedSeconds: () => number;
  resetTimer: () => void;
  sessionStartedAt: EpochMs;
  reviewSlotRevisions?: ReviewSlotRevision[];
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const savedRevisionsRef = useRef(new Map<string, number | null>());
  const [verifyPeriodsWithEventsMutation] =
    useUploadCorrectedEventsAndVerifyMutation();

  const verifyPeriods = useCallback(
    async (
      reviewPeriods: ReviewPeriodData[],
      eventsInPeriods: ScrapEyesEventWithReviewData[],
      originalEventsInPeriods: ScrapEyesEventWithReviewData[],
      options?: {
        includeVerified?: boolean;
        verifyPeriodStarts?: string[];
        reviewSlotRevisions?: ReviewSlotRevision[];
      }
    ) => {
      setIsVerifying(true);
      try {
        const periodIds = reviewPeriods
          .filter(
            (period) =>
              options?.includeVerified === true || period.verified_at == null
          )
          .map((period) => period.id);

        const correctedEvents = dedupeClientIdentifiedEvents(
          dedupeById(eventsInPeriods).map(toClientIdentifiedReviewEvent)
        );
        const originalEvents = dedupeById(originalEventsInPeriods).map(
          toClientIdentifiedReviewEvent
        );
        const expectedRevisions = expectedSlotRevisions({
          snapshots: [reviewSlotRevisions, options?.reviewSlotRevisions],
          periods: reviewPeriods.filter((period) =>
            periodIds.includes(period.id)
          ),
          correctedEvents,
          verifyPeriodStarts: options?.verifyPeriodStarts ?? [],
        });
        if (
          expectedRevisions?.some((revision) => {
            const key = `${tenantName}|${location}|${canonicalInstant(revision.start)}`;
            return (
              savedRevisionsRef.current.has(key) &&
              savedRevisionsRef.current.get(key) === revision.revision_id
            );
          })
        )
          throw new ReviewRefreshRequiredError(
            "Refresh review events before saving again"
          );
        const activeReviewSeconds = getElapsedSeconds();

        await verifyPeriodsWithEventsMutation({
          tenantName,
          correctedEventsForPeriods: {
            period_ids: periodIds,
            ...(expectedRevisions === undefined
              ? {}
              : { expected_slot_revisions: expectedRevisions }),
            original_events: originalEvents,
            corrected_events: correctedEvents,
            active_review_seconds: activeReviewSeconds,
            session_started_at: new Date(sessionStartedAt).toISOString(),
            location,
            verify_period_starts: options?.verifyPeriodStarts ?? [],
          },
        }).unwrap();

        expectedRevisions?.forEach((revision) =>
          savedRevisionsRef.current.set(
            `${tenantName}|${location}|${canonicalInstant(revision.start)}`,
            revision.revision_id
          )
        );
        resetTimer();
      } finally {
        setIsVerifying(false);
      }
    },
    [
      verifyPeriodsWithEventsMutation,
      tenantName,
      location,
      getElapsedSeconds,
      resetTimer,
      sessionStartedAt,
      reviewSlotRevisions,
    ]
  );

  return {
    verifyPeriods,
    isVerifying,
  };
};
